import React, {
  Fragment,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { Option } from "react-select/src/filters";

import { Container, Icon, Link, Pressable, Row } from "../components";
import { PulseLoader } from "../components/pulse_loader";
import {
  Amenity,
  getMaxCapacity,
  Location,
  Pricing,
  PricingType,
  Space,
} from "../data";
import {
  useGetLightPartnerOffering,
  useGetPartnerOffering,
  useGetLocationsByPartnerOfferingSlug,
} from "../data/api";
import { PartnerOfferingLocation } from "../data/partner_offering";
import { buildPathWithParams, UrlParams, useQueryString } from "../hooks";
import { formatCurrency } from "../lib/currency/currency";
import { FirstDayOfWeek } from "../lib/datetime";
import { useI18n } from "../lib/i18n/i18n_provider";
import { groupBy, uniqueBy } from "../lib/js_utils/array_utils";
import { isEmpty } from "../lib/js_utils/lang_utils";
import {
  PhysicalSpace,
  useLocationV2Query,
  useLocationPhysicalSpacesQuery,
  useSpaceV2Query,
} from "./api";
import { Button } from "./button";
import { DateRangePicker } from "./date_range_picker";
import { isWeekend } from "./date_utils";
import { isBefore, toDay, today } from "./day_utils";
import { Dialog } from "./dialog";
import { Divider } from "./divider";
import { formatDayRange } from "./format_date_range";
import { Grid } from "./grid";
import { Heading } from "./heading";
import { useIsWework } from "./hooks/use_is_wework";
import { DayPassDateRange } from "./order_params";
import { PartnerConfig } from "./partner_config";
import { Spacer } from "./spacer";
import { FAQ } from "./template_offering_details_base";
import {
  Capacity,
  CapacityPicker,
  CapacityPickerMobile,
  CityPicker,
  CityPickerMobile,
  EmptyState,
  ItemCardHeader,
  TemplateOfferingListingLayout,
} from "./template_offering_listing_base";
import { Text } from "./text";

interface TemplateOfferingListingDayOfficeProps {
  firstDayOfWeek: FirstDayOfWeek;
  showTalkWithUsPrompt?: boolean;
  viewMoreLabel?: string;
  partnerConfig: PartnerConfig;
  data: {
    faqs?: FAQ[];
    description?: React.ReactNode;
  };
}

interface TemplateOfferingListingDayOfficeParams {
  offeringID: string;
}

export function TemplateOfferingListingDayOffice(
  props: TemplateOfferingListingDayOfficeProps
) {
  const {
    showTalkWithUsPrompt,
    partnerConfig,
    data,
    firstDayOfWeek,
    viewMoreLabel,
  } = props;
  const {
    favicon,
    title,
    hubspotForm,
    showLanguageSelector = false,
  } = partnerConfig;
  const { faqs, description } = data;
  const pageParams = useParams<TemplateOfferingListingDayOfficeParams>();
  const history = useHistory();
  const searchParams = useQueryString<DayOfficeFilterSearchParams>();
  const [filter, setFilter] = useState<DayOfficeFilter>(
    deserializeDayOfficeFilterSearchParams(searchParams)
  );
  const partnerOfferingSlug = pageParams.offeringID;
  const partnerOffering = useGetLightPartnerOffering(partnerOfferingSlug);
  const locations = useGetLocationsByPartnerOfferingSlug(partnerOfferingSlug);

  const handleCityChange = useCallback((city?: string) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      city,
      locationID: undefined,
    }));
  }, []);
  const handlePressResetFilter = useCallback(() => {
    setFilter({
      city: undefined,
      minCapacity: undefined,
      maxCapacity: undefined,
      startDate: undefined,
      endDate: undefined,
    });
  }, []);

  const handleCapacityChange = useCallback(
    (capacity?: { minCapacity?: number; maxCapacity?: number }) => {
      if (!capacity) {
        setFilter((prevFilter) => ({
          ...prevFilter,
          minCapacity: undefined,
          maxCapacity: undefined,
        }));
      } else {
        setFilter((prevFilter) => ({
          ...prevFilter,
          minCapacity: capacity.minCapacity,
          maxCapacity: capacity.maxCapacity,
        }));
      }
    },
    []
  );
  const handleDateRangeChange = useCallback((dateRange?: DayPassDateRange) => {
    if (!dateRange) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        startDate: undefined,
        endDate: undefined,
      }));
    } else {
      setFilter((prevFilter) => ({
        ...prevFilter,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }));
    }
  }, []);

  useEffect(() => {
    const pathWithParams = buildPathWithParams(
      window.location.pathname,
      filter
    );
    history.replace(pathWithParams);
  }, [filter, history]);

  const cities = uniqueBy(
    locations.map((l) => ({
      value: l.city as string,
      label: l.city as string,
      data: l,
    })),
    (l) => l.value
  );

  const hasFilter = !!(
    filter.minCapacity ||
    filter.maxCapacity ||
    (filter.startDate && filter.startDate !== today()) ||
    (filter.endDate && filter.endDate !== today()) ||
    filter.city
  );

  return (
    <TemplateOfferingListingLayout
      title={title || partnerOffering.partner.name}
      description={description}
      favicon={favicon}
      showLanguageSelector={showLanguageSelector}
      partnerConfig={partnerConfig}
      showTalkWithUsPrompt={showTalkWithUsPrompt}
      hubspotForm={hubspotForm}
      offeringID={pageParams.offeringID}
      faqs={faqs}
      partnerOffering={partnerOffering}
      desktopFilters={
        <DesktopFilters
          onCapacityChange={handleCapacityChange}
          onCityChange={handleCityChange}
          onDateRangeChange={handleDateRangeChange}
          filter={filter}
          cities={cities}
          firstDayOfWeek={firstDayOfWeek}
        />
      }
      mobileFilters={
        <MobileFilters
          cities={cities}
          onFilterChange={setFilter}
          filter={filter}
          firstDayOfWeek={firstDayOfWeek}
          hasFilter={hasFilter}
        />
      }
      listingContent={
        <Container>
          <Suspense fallback={<PulseLoader />}>
            <DayOfficeGallery
              partnerOfferingSlug={pageParams.offeringID}
              filter={filter}
              hasFilter={hasFilter}
              onPressResetFilter={handlePressResetFilter}
              viewMoreLabel={viewMoreLabel}
            />
          </Suspense>
        </Container>
      }
    />
  );
}

interface DayOfficeGalleryProps {
  filter: DayOfficeFilter;
  hasFilter: boolean;
  partnerOfferingSlug: string;
  onPressResetFilter: () => void;
  viewMoreLabel?: string;
}

function DayOfficeGallery(props: DayOfficeGalleryProps) {
  const {
    filter,
    hasFilter,
    partnerOfferingSlug,
    onPressResetFilter,
    viewMoreLabel,
  } = props;
  const partnerOffering = useGetPartnerOffering(partnerOfferingSlug, filter);

  if (isEmpty(partnerOffering.offeringLocations)) {
    return (
      <EmptyState
        hasFilter={hasFilter}
        onPressResetFilter={onPressResetFilter}
      />
    );
  }

  return (
    <CityLocations
      partnerOfferingSlug={partnerOfferingSlug}
      partnerOfferingLocations={partnerOffering.offeringLocations}
      filter={filter}
      viewMoreLabel={viewMoreLabel}
    />
  );
}

interface CityLocationsProps {
  filter: DayOfficeFilter;
  partnerOfferingSlug: string;
  partnerOfferingLocations: PartnerOfferingLocation[];
  viewMoreLabel?: string;
}

function CityLocations(props: CityLocationsProps) {
  const {
    partnerOfferingLocations,
    partnerOfferingSlug,
    filter,
    viewMoreLabel,
  } = props;
  const locationsGroupedByLocation = useMemo(() => {
    return groupBy(partnerOfferingLocations, (l) => l.location.id || "");
  }, [partnerOfferingLocations]);

  const featuredPromoDescription = function (locationID: string) {
    return partnerOfferingLocations.find((p) => p.location.id === locationID)
      ?.featuredPromoDescription;
  };

  return (
    <Container>
      <Grid itemsPerRow={{ md: 3, default: 1 }} spacing={16}>
        {Object.keys(locationsGroupedByLocation).map((locationID) => {
          return (
            <CityLocation
              key={locationID}
              locationID={locationID}
              filter={filter}
              partnerOfferingSlug={partnerOfferingSlug}
              viewMoreLabel={viewMoreLabel}
              featuredPromoDescription={featuredPromoDescription(locationID)}
            />
          );
        })}
      </Grid>
    </Container>
  );
}

interface CityLocationProps {
  locationID: string;
  filter: DayOfficeFilter;
  partnerOfferingSlug: string;
  viewMoreLabel?: string;
  featuredPromoDescription?: string;
}

function CityLocation(props: CityLocationProps) {
  const {
    locationID,
    filter,
    partnerOfferingSlug,
    viewMoreLabel,
    featuredPromoDescription,
  } = props;

  return (
    <Container height="100%" paddingBottom={16}>
      <Container
        flex={1}
        borderWidth={1}
        borderRadius={5}
        borderColor="#D7D5DD"
      >
        <Suspense
          fallback={
            <Container minHeight={240} center>
              <PulseLoader />
            </Container>
          }
        >
          <DayOfficeGalleryItem
            filter={filter}
            partnerOfferingSlug={partnerOfferingSlug}
            locationID={locationID}
            viewMoreLabel={viewMoreLabel}
            featuredPromoDescription={featuredPromoDescription}
          />
        </Suspense>
      </Container>
    </Container>
  );
}
interface DayOfficeGalleryItemProps {
  filter: DayOfficeFilter;
  partnerOfferingSlug: string;
  locationID: string;
  viewMoreLabel?: string;
  featuredPromoDescription?: string;
}

function DayOfficeGalleryItem(props: DayOfficeGalleryItemProps) {
  const {
    locationID,
    partnerOfferingSlug,
    filter,
    viewMoreLabel,
    featuredPromoDescription,
  } = props;
  const location = useLocationV2Query(locationID);
  const { locale, fbt } = useI18n();
  return (
    <Container flex={1}>
      <ItemCardHeader
        coverImage={{ src: location.images[0].small.url, alt: location.name }}
        title={location.name}
        largeTitle
        subTitle={`${location.streetAddress}, ${location.city}`}
        featuredPromoDescription={featuredPromoDescription}
      />
      <Container paddingHorizontal={8} paddingBottom={8}>
        {filter.startDate && filter.endDate && (
          <Fragment>
            <Spacer size={8} />
            <Text weight="bold">
              {fbt(
                "Available on" +
                  " " +
                  fbt.param(
                    "date",
                    formatDayRange(
                      {
                        start: filter.startDate,
                        end: filter.endDate,
                      },
                      fbt,
                      locale
                    )
                  )
              )}
            </Text>
          </Fragment>
        )}
      </Container>
      <Divider />
      <Suspense
        fallback={
          <Container minHeight={120} center>
            <PulseLoader />
          </Container>
        }
      >
        <DayOfficePhysicalSpaces
          filter={filter}
          partnerOfferingSlug={partnerOfferingSlug}
          location={location}
          viewMoreLabel={viewMoreLabel}
        />
      </Suspense>
    </Container>
  );
}

interface DayOfficePhysicalSpacesProps {
  filter: DayOfficeFilter;
  partnerOfferingSlug: string;
  location: Location;
  viewMoreLabel?: string;
}

function DayOfficePhysicalSpaces(props: DayOfficePhysicalSpacesProps) {
  const { filter, partnerOfferingSlug, location, viewMoreLabel } = props;
  const [open, setOpen] = useState(false);
  const { fbt } = useI18n();
  const physicalSpaces = useLocationPhysicalSpacesQuery(location.id, filter);

  const sortedPhysicalSpaces = useMemo(() => {
    const clonedSpaces = physicalSpaces.slice();

    clonedSpaces.sort(
      (a, b) => getMaxCapacity(a.space) - getMaxCapacity(b.space)
    );

    return clonedSpaces;
  }, [physicalSpaces]);

  const firstSpaceID = sortedPhysicalSpaces[0].spaceID;
  const firstSpace = useSpaceV2Query(firstSpaceID);

  return (
    <Fragment>
      <Container padding={8} paddingTop={16} flex={1}>
        <Fragment>
          <DayOfficeGalleryItemSpaceRow
            partnerOfferingSlug={partnerOfferingSlug}
            filter={filter}
            space={firstSpace}
            physicalSpace={sortedPhysicalSpaces[0]}
          />
          <Spacer size={16} />
        </Fragment>
      </Container>
      <ViewMoreButton
        physicalSpaces={physicalSpaces}
        onViewMore={() => setOpen(true)}
        viewMoreLabel={viewMoreLabel}
      />
      <Dialog isOpen={open} onRequestClose={() => setOpen(false)}>
        <div className="listing-day-office-dialog-wrapper">
          <Pressable onPress={() => setOpen(false)}>
            <Icon name="x" />
          </Pressable>
          <Spacer size={24} />
          <Heading>
            {fbt("Day offices at-" + fbt.param("locationName", location.name))}
          </Heading>
          <Spacer size={8} />
          <FilterPreview filter={filter} />
          <Spacer size={32} />
          <Container>
            {sortedPhysicalSpaces.map((ps) => {
              return (
                <Fragment key={ps.id}>
                  <DayOfficeGalleryItemSpacePreview
                    partnerOfferingSlug={partnerOfferingSlug}
                    filter={filter}
                    physicalSpace={ps}
                  />
                  <Spacer size={16} />
                </Fragment>
              );
            })}
          </Container>
          <Spacer size={24} />
          <Spacer />
          <Button onPress={() => setOpen(false)} title={fbt("Close")} />
        </div>
      </Dialog>
    </Fragment>
  );
}

interface ViewMoreButtonProps {
  physicalSpaces: PhysicalSpace[];
  onViewMore: () => void;
  viewMoreLabel?: string;
}

function ViewMoreButton(props: ViewMoreButtonProps) {
  const { onViewMore, physicalSpaces, viewMoreLabel } = props;
  const { fbt } = useI18n();

  return (
    <Fragment>
      {physicalSpaces.length > 1 ? (
        <Pressable onPress={onViewMore}>
          <div
            style={{
              height: 40,
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
              backgroundColor: "#F7F7F7",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text>
              {fbt(viewMoreLabel || "View More")} ({physicalSpaces.length - 1})
            </Text>
          </div>
        </Pressable>
      ) : (
        <div
          style={{
            height: 40,
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            backgroundColor: "#F7F7F7",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        />
      )}
    </Fragment>
  );
}
interface FilterPreviewProps {
  filter: DayOfficeFilter;
}

function FilterPreview(props: FilterPreviewProps) {
  const { filter } = props;
  const { minCapacity, maxCapacity } = filter;
  const { locale, fbt } = useI18n();
  return (
    <Row alignItems="center">
      <Row alignItems="center">
        <Icon name="capacity" />
        <Spacer size={4} direction="horizontal" />
        <Text>
          {minCapacity && maxCapacity && `${minCapacity} - ${maxCapacity}`}
          {minCapacity && !maxCapacity && `${minCapacity}`}
          {!minCapacity && !maxCapacity && fbt("Any")}
        </Text>
      </Row>

      {filter.startDate && filter.endDate && (
        <Fragment>
          <Spacer size={24} direction="horizontal" />
          <Row alignItems="center">
            <Icon name="calendar" size={20} />
            <Spacer size={4} direction="horizontal" />
            <Text>
              {fbt(
                "Available on" +
                  " " +
                  fbt.param(
                    "date",
                    formatDayRange(
                      {
                        start: filter.startDate,
                        end: filter.endDate,
                      },
                      fbt,
                      locale
                    )
                  )
              )}
            </Text>
          </Row>
        </Fragment>
      )}
    </Row>
  );
}

interface DayOfficeGalleryItemSpacePreviewProps {
  filter: DayOfficeFilter;
  partnerOfferingSlug: string;
  physicalSpace: PhysicalSpace;
}

function DayOfficeGalleryItemSpacePreview(
  props: DayOfficeGalleryItemSpacePreviewProps
) {
  const { physicalSpace, filter, partnerOfferingSlug } = props;
  const spaceID = physicalSpace.spaceID;
  const space = useSpaceV2Query(spaceID);
  const { locale, fbt } = useI18n();

  let offeringDetailsUrl = buildPathWithParams(
    `/offerings/${partnerOfferingSlug}/spaces/${space.id}?physicalSpaceID=${physicalSpace.id}`,
    filter
  );

  return (
    <Container
      key={physicalSpace.id}
      borderWidth={1}
      borderRadius={5}
      borderColor="#D7D5DD"
    >
      <Link
        className="listing-day-office-space-preview"
        to={offeringDetailsUrl}
        onPress={() => {
          setTimeout(() => {
            window.scrollTo({ top: 0 });
          }, 0);
        }}
      >
        <Row>
          <Container flex={3}>
            <img
              alt={`Office ${physicalSpace.name}`}
              style={{ objectFit: "cover", width: "100%", height: 116 }}
              src={space.images[0].medium.url}
            />
          </Container>
          <Container flex={4} padding={16}>
            <Container>
              <Row alignItems="center" justifyContent="space-between">
                <Text className="listing-day-office-space-name">
                  {fbt("Office")} {physicalSpace.name}
                </Text>
                <Row>
                  <Text className="listing-day-office-book" color="link">
                    {fbt("Book")}
                  </Text>
                  <Icon
                    className="text-color-link"
                    size={16}
                    name={
                      locale.direction === "ltr" ? "arrow-right" : "arrow-left"
                    }
                  />
                </Row>
              </Row>
            </Container>
            <Spacer size={4} />
            <Container>
              <Attributes space={space} physicalSpace={physicalSpace} />
            </Container>
            <Spacer size={4} />
            <Container>
              <DayOfficePricing
                currency={space.currencyCode}
                pricing={space.pricings[0]}
              />
            </Container>
          </Container>
        </Row>
      </Link>
    </Container>
  );
}

interface AttributesProps {
  space: Space;
  physicalSpace: PhysicalSpace;
}

function Attributes(props: AttributesProps) {
  const { space, physicalSpace } = props;
  const { fbt } = useI18n();
  return (
    <Row alignItems="center" wrap>
      <Capacity capacity={getMaxCapacity(space)} color="muted" />{" "}
      <Spacer size={4} direction="horizontal" />
      {physicalSpace.floor && (
        <Text color="muted">
          {" "}
          ∙ {fbt("Floor")} {physicalSpace.floor}
        </Text>
      )}
      <Spacer size={4} direction="horizontal" />
      {space.attributes.withWindow && (
        <Text color="muted"> ∙ {fbt("With window")}</Text>
      )}
    </Row>
  );
}

interface DayOfficeGalleryItemSpaceRowProps {
  space: Space;
  partnerOfferingSlug: string;
  filter: DayOfficeFilter;
  physicalSpace: PhysicalSpace;
}

function DayOfficeGalleryItemSpaceRow(
  props: DayOfficeGalleryItemSpaceRowProps
) {
  const { space, partnerOfferingSlug, physicalSpace, filter } = props;
  const { fbt, locale } = useI18n();
  if (!physicalSpace) {
    return <Text>{fbt("No available space")}</Text>;
  }

  let offeringDetailsUrl = buildPathWithParams(
    `/offerings/${partnerOfferingSlug}/spaces/${space.id}?physicalSpaceID=${physicalSpace.id}`,
    filter
  );

  return (
    <Link
      className="listing-day-office-space"
      to={offeringDetailsUrl}
      onPress={() => {
        setTimeout(() => {
          window.scrollTo({ top: 0 });
        }, 0);
      }}
    >
      <Container>
        <Container>
          <Row alignItems="center" justifyContent="space-between">
            <Text className="listing-day-office-space-name">
              {fbt("Office")} {physicalSpace.name}
            </Text>
            <Row alignItems="center">
              <Text className="listing-day-office-book" color="link">
                {fbt("Book")}
              </Text>
              <Icon
                className="text-color-link"
                size={16}
                name={locale.direction === "ltr" ? "arrow-right" : "arrow-left"}
              />
            </Row>
          </Row>
        </Container>
        <Spacer size={4} />
        <Container>
          <Attributes space={space} physicalSpace={physicalSpace} />
        </Container>
        <Spacer size={4} />
        <Container>
          <DayOfficePricing
            currency={space.currencyCode}
            pricing={space.pricings[0]}
          />
        </Container>
      </Container>
    </Link>
  );
}

interface DayOfficePricingProps {
  pricing: Pricing;
  currency: string | null;
}

export function DayOfficePricing(props: DayOfficePricingProps) {
  const { pricing, currency } = props;
  const { locale, fbt } = useI18n();
  const isWeWork = useIsWework();

  return (
    <Text>
      <Text weight="bold">
        {formatCurrency(pricing.price, currency, locale.languageTag)}
      </Text>

      {isWeWork
        ? pricing.type === PricingType.Daily
          ? ` ${fbt("before VAT per day")}`
          : ` ${fbt("before VAT per hour")}`
        : pricing.type === PricingType.Daily
        ? fbt("/day")
        : fbt("/hr")}
    </Text>
  );
}

interface AmenitiesProps {
  amenities: Amenity[];
}

export function Amenities(props: AmenitiesProps) {
  const { amenities } = props;

  return (
    <Row wrap>
      {amenities.map((amenity) => (
        <React.Fragment key={amenity.name}>
          <img
            style={{ width: 24, height: 24 }}
            src={amenity.iconURL}
            alt={amenity.name}
          />
          <Spacer size={8} direction="horizontal" />
        </React.Fragment>
      ))}
    </Row>
  );
}

interface DesktopFiltersProps {
  onCityChange: (city?: string) => void;
  onDateRangeChange: (dateRange?: DayPassDateRange) => void;
  onCapacityChange: (capacity?: {
    minCapacity?: number;
    maxCapacity?: number;
  }) => void;
  firstDayOfWeek: FirstDayOfWeek;
  filter: DayOfficeFilter;
  cities: Option[];
}

function DesktopFilters(props: DesktopFiltersProps) {
  const {
    filter,
    cities,
    firstDayOfWeek,
    onDateRangeChange,
    onCapacityChange,
    onCityChange,
  } = props;
  const { fbt } = useI18n();

  return (
    <Row>
      <Container maxWidth={214} flex={1}>
        <CityPicker
          onChange={onCityChange}
          cities={cities}
          value={filter.city}
        />
      </Container>
      <Spacer size={16} direction="horizontal" />
      <Container maxWidth={200} flex={1}>
        <CapacityPicker onChange={onCapacityChange} value={filter} />
      </Container>
      <Spacer size={16} direction="horizontal" />
      <div
        style={{ maxWidth: 240, flex: 1 }}
        className={
          "day-office-date-picker-container " +
          (filter.startDate ? "has-data" : "")
        }
      >
        <DateRangePicker
          firstDayOfWeek={firstDayOfWeek}
          view="dialog"
          ctaTitle={fbt("Save")}
          value={filter}
          forceInitialSingleDate={
            filter.startDate === today() && filter.startDate === filter.endDate
          }
          isBlockedDate={(date) => isWeekend(date, firstDayOfWeek)}
          isOutsideRange={(date) => isBefore(toDay(date), today())}
          onNextVisibleDates={() => false}
          onComplete={onDateRangeChange}
        />
      </div>
    </Row>
  );
}

interface MobileFiltersProps {
  firstDayOfWeek: FirstDayOfWeek;
  onFilterChange: (filter: DayOfficeFilter) => void;
  filter: DayOfficeFilter;
  hasFilter: boolean;
  cities: Option[];
}

function MobileFilters(props: MobileFiltersProps) {
  const { cities, hasFilter, filter, firstDayOfWeek, onFilterChange } = props;
  const [localFilter, setLocalFilter] = useState(filter);
  const [advanced, setAdvanced] = useState(false);
  const { fbt } = useI18n();
  const handleCityChange = useCallback((city?: string) => {
    setLocalFilter((prevFilter) => ({
      ...prevFilter,
      city,
      locationID: undefined,
    }));
  }, []);

  const handleMainCityChange = useCallback(
    (city?: string) => {
      setLocalFilter((prevFilter) => ({
        ...prevFilter,
        city,
      }));
      onFilterChange({
        ...localFilter,
        city,
      });
    },
    [localFilter, onFilterChange]
  );
  const handleDateRangeChange = useCallback((dateRange?: DayPassDateRange) => {
    if (!dateRange) {
      setLocalFilter((prevFilter) => ({
        ...prevFilter,
        startDate: undefined,
        endDate: undefined,
      }));
    } else {
      setLocalFilter((prevFilter) => ({
        ...prevFilter,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }));
    }
  }, []);
  const handleCapacityChange = useCallback(
    (capacity?: { minCapacity?: number; maxCapacity?: number }) => {
      if (!capacity) {
        setLocalFilter((prevFilter) => ({
          ...prevFilter,
          minCapacity: undefined,
          maxCapacity: undefined,
        }));
      } else {
        setLocalFilter((prevFilter) => ({
          ...prevFilter,
          ...capacity,
        }));
      }
    },
    []
  );

  const handleSubmitFilter = useCallback(() => {
    setAdvanced(false);
    onFilterChange(localFilter);
  }, [localFilter, onFilterChange]);

  if (!advanced) {
    return (
      <Container
        backgroundColor="white"
        boxShadow="0px 2px 7px 3px rgba(0, 0, 0, 0.25)"
        padding={16}
      >
        <Row>
          <Container flex={1}>
            <CityPickerMobile
              onChange={handleMainCityChange}
              cities={cities}
              value={filter.city}
            />
          </Container>
          <Spacer size={16} direction="horizontal" />
          <Container center>
            <Pressable onPress={() => setAdvanced(true)}>
              <Icon name="filter" />
            </Pressable>
            {hasFilter && (
              <div
                style={{
                  position: "absolute",
                  width: 8,
                  height: 8,
                  backgroundColor: "#000",
                  top: 0,
                  right: 0,
                  borderRadius: 999,
                }}
              />
            )}
          </Container>
        </Row>
      </Container>
    );
  }

  return (
    <Container
      backgroundColor="white"
      boxShadow="0px 2px 7px 3px rgba(0, 0, 0, 0.25)"
      padding={16}
    >
      <Container>
        <Pressable
          style={{ position: "absolute", left: 4 }}
          onPress={() => setAdvanced(false)}
        >
          <Icon name="x" />
        </Pressable>
        <Heading align="center">{fbt("Filters")}</Heading>
      </Container>
      <Spacer size={16} />
      <Container>
        <CityPickerMobile
          onChange={handleCityChange}
          cities={cities}
          value={localFilter.city}
        />
      </Container>
      <Spacer size={8} />
      <Container>
        <CapacityPickerMobile
          onChange={handleCapacityChange}
          value={localFilter}
        />
      </Container>
      <Spacer size={8} />
      <Container>
        <DateRangePicker
          firstDayOfWeek={firstDayOfWeek}
          view="modal"
          ctaTitle={fbt("Save")}
          value={localFilter}
          forceInitialSingleDate={
            filter.startDate === today() && filter.startDate === filter.endDate
          }
          isBlockedDate={(date) => isWeekend(date, firstDayOfWeek)}
          isOutsideRange={(date) => isBefore(toDay(date), today())}
          onNextVisibleDates={() => false}
          onComplete={handleDateRangeChange}
          override={{
            view: {
              style: {
                height: 40,
                border: `1px solid hsl(0,0%,80%)`,
                borderRadius: 4,
                paddingLeft: 8,
              },
            },
          }}
        />
      </Container>
      <Spacer size={16} />
      <Button onPress={handleSubmitFilter} title={fbt("Filter")} />
    </Container>
  );
}

interface DayOfficeFilterSearchParams {
  city?: string;
  minCapacity?: string;
  maxCapacity?: string;
  startDate?: string;
  endDate?: string;
  /* Additional params support manage navigation from catalog page */
  fromCatalog?: string;
}

interface DayOfficeFilter extends UrlParams {
  city?: string;
  minCapacity?: number;
  maxCapacity?: number;
  startDate?: string;
  endDate?: string;
  /* Additional params support manage navigation from catalog page */
  fromCatalog?: string;
}

function deserializeDayOfficeFilterSearchParams(
  searchParams?: DayOfficeFilterSearchParams
): DayOfficeFilter {
  let startDate = searchParams?.startDate;
  let endDate = searchParams?.endDate;

  if (startDate && isBefore(startDate, today())) {
    startDate = today();
  }
  if (endDate && isBefore(endDate, today())) {
    endDate = today();
  }

  return {
    city: searchParams?.city,
    minCapacity: searchParams?.minCapacity
      ? Number(searchParams?.minCapacity)
      : undefined,
    maxCapacity: searchParams?.maxCapacity
      ? Number(searchParams?.maxCapacity)
      : undefined,
    startDate,
    endDate,
    fromCatalog: searchParams?.fromCatalog,
  };
}
